import React from 'react';
import { AlertTriangle, CheckCircle2, Database, X } from 'lucide-react';

function listValue(value) {
  if (!value) return '';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '';
  return String(value);
}

function statusChanges(preview) {
  if (preview?.status_changes?.length) return preview.status_changes;
  if (preview?.affected_deliveries?.length) {
    return preview.affected_deliveries.map(item => ({
      delivery_id: item.delivery_point_id || item.id,
      from: item.from_status,
      to: item.to_status,
    }));
  }
  return preview?.mutation_summary?.status_changes || [];
}

function affectedIds(preview) {
  return preview?.affected_delivery_ids
    || preview?.changed_ids
    || preview?.delivery_ids
    || (preview?.affected_deliveries || []).map(item => item.delivery_point_id || item.id).filter(Boolean)
    || preview?.mutation_summary?.affected_delivery_ids
    || [];
}

export function ActionConfirmationModal({ preview, isExecuting, error, onCancel, onConfirm }) {
  if (!preview) return null;
  const ids = affectedIds(preview);
  const changes = statusChanges(preview);
  const mutates = Boolean(preview.mutates || preview.ui_action === 'confirm_api_call' || ids.length || changes.length);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-extrabold text-slate-950">
              {mutates ? <Database className="h-5 w-5 text-red-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
              Confirmar acción operacional
            </h2>
            <p className="mt-1 text-xs text-slate-500">Nada se ejecuta hasta presionar Confirmar.</p>
          </div>
          <button onClick={onCancel} className="rounded-lg bg-slate-100 p-2 text-slate-500 hover:bg-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4 text-sm">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Action name</p>
            <p className="font-extrabold text-slate-900">{preview.label || preview.action_type}</p>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Affected delivery IDs</p>
              <p className="mt-1 text-xs font-bold text-slate-800">{listValue(ids)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Reason</p>
              <p className="mt-1 text-xs font-bold text-slate-800">{preview.reason || preview.payload?.reason || 'Admin-confirmed command center action'}</p>
            </div>
          </div>

          {changes.length > 0 && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-orange-700">Status changes</p>
              <div className="mt-2 space-y-1">
                {changes.map((change, index) => (
                  <p key={`${change.delivery_id || index}-${change.to || change.new_status}`} className="text-xs font-bold text-orange-900">
                    #{change.delivery_id || change.id || '?'}: {change.from || change.old_status || 'actual'} ? {change.to || change.new_status || 'nuevo'}
                  </p>
                ))}
              </div>
            </div>
          )}

          {preview.preview_text && (
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Preview</p>
              <p className="mt-1 whitespace-pre-wrap text-xs font-semibold text-slate-700">{preview.preview_text}</p>
            </div>
          )}

          {mutates && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-800">
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="text-xs font-extrabold uppercase">Mutation warning</p>
                  <p className="mt-1 text-xs font-semibold">
                    Esta acción puede cambiar estados, enviar mensajes o registrar eventos operativos. Verifica IDs y motivo antes de confirmar.
                  </p>
                </div>
              </div>
            </div>
          )}

          {(preview.warnings || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {preview.warnings.map(flag => (
                <span key={flag} className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-extrabold text-amber-800">{flag}</span>
              ))}
            </div>
          )}

          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700">{error}</div>}
        </div>

        <div className="flex gap-2 border-t border-slate-200 p-4">
          <button onClick={onCancel} disabled={isExecuting} className="flex-1 rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={isExecuting} className="flex-1 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-bold text-white hover:bg-[#9d2d62] disabled:opacity-50">
            {isExecuting ? 'Ejecutando...' : 'Confirmar ejecución'}
          </button>
        </div>
      </div>
    </div>
  );
}
